import { useState, type FormEvent } from 'react';
import type { ExpenseCategory, InflowFrequency } from '@economi/domain-contracts';
import {
  centsToSoles,
  computeSolTemporalKpis,
  EXPENSE_CATEGORY_LABELS,
  formatSolesFromCents,
  getRecurringExpenseAmountForMonthCents,
  INFLOW_FREQUENCY_LABELS,
  isRecurringExpenseApplicableInCycle,
  type CreditCardProfileDto,
  type FinancialCycleState,
  type RecurringExpenseItem,
  type RecurringValidityMode,
} from '../../services/solImpactEngine';
import { InfoPopover } from '../InfoPopover/InfoPopover';
import styles from './RecurringExpensesView.module.css';

interface RecurringExpensesViewProps {
  readonly cycleState: FinancialCycleState;
  readonly creditCards?: ReadonlyArray<CreditCardProfileDto>;
  readonly onAddRecurringExpense: (params: {
    name: string;
    merchantName: string;
    issuerRuc?: string;
    category: ExpenseCategory;
    frequency: InflowFrequency;
    customIntervalDays?: number;
    defaultAmountSoles: number;
    billingDayOfMonth: number;
    isInevitable: boolean;
    validityMode: RecurringValidityMode;
    expirationDateIso?: string | null;
    isCreditCardExpense: boolean;
    creditCardId?: string | null;
  }) => void;
  readonly onUpdateRecurringExpense: (
    ruleId: string,
    params: {
      name: string;
      merchantName: string;
      issuerRuc: string | null;
      category: ExpenseCategory;
      frequency: InflowFrequency;
      customIntervalDays?: number;
      defaultAmountSoles: number;
      billingDayOfMonth: number;
      isInevitable: boolean;
      validityMode?: RecurringValidityMode;
      expirationDateIso?: string | null;
      isCreditCardExpense?: boolean;
      creditCardId?: string | null;
      isActive: boolean;
    }
  ) => void;
  readonly onDeleteRecurringExpense: (ruleId: string) => void;
  readonly onUpdateRecurringExpenseForMonth: (params: {
    ruleId: string;
    monthIso: string;
    amountSoles: number;
    applyAsNewDefault: boolean;
    clearMonthOverride?: boolean;
    isInevitable?: boolean;
  }) => void;
  readonly onOpenInflowsView?: () => void;
}

function shiftMonthIso(monthIso: string, deltaMonths: number): string {
  const parts = monthIso.split('-');
  const year = Number(parts[0]) || 2026;
  const month = Number(parts[1]) || 10;
  const date = new Date(Date.UTC(year, month - 1 + deltaMonths, 1));
  const nextY = date.getUTCFullYear();
  const nextM = String(date.getUTCMonth() + 1).padStart(2, '0');
  return `${nextY}-${nextM}`;
}

export function RecurringExpensesView({
  cycleState,
  creditCards: creditCardsProp,
  onAddRecurringExpense,
  onUpdateRecurringExpense,
  onDeleteRecurringExpense,
  onUpdateRecurringExpenseForMonth,
  onOpenInflowsView,
}: RecurringExpensesViewProps) {
  const creditCards = creditCardsProp ?? cycleState.creditCards ?? [];
  const activeCycleMonthIso = cycleState.startDateIso.slice(0, 7);
  const [selectedMonthIso, setSelectedMonthIso] =
    useState<string>(activeCycleMonthIso);

  // Estado del formulario de creación de Nuevo Gasto Recurrente
  const [rexpName, setRexpName] = useState<string>('');
  const [rexpMerchant, setRexpMerchant] = useState<string>('');
  const [rexpRuc, setRexpRuc] = useState<string>('');
  const [rexpCategory, setRexpCategory] =
    useState<ExpenseCategory>('UTILITIES');
  const [rexpFrequency, setRexpFrequency] =
    useState<InflowFrequency>('MONTHLY');
  const [rexpCustomDays, setRexpCustomDays] = useState<number>(30);
  const [rexpDefaultSoles, setRexpDefaultSoles] = useState<string>('');
  const [rexpDay, setRexpDay] = useState<number>(15);
  const [rexpIsInevitable, setRexpIsInevitable] = useState<boolean>(true);
  const [rexpValidityMode, setRexpValidityMode] =
    useState<RecurringValidityMode>('INDEFINITE');
  const [rexpExpirationDateIso, setRexpExpirationDateIso] =
    useState<string>('');
  const [rexpSelectedCardId, setRexpSelectedCardId] = useState<string>('');

  // Estado para edición por mes
  const [draftMonthAmounts, setDraftMonthAmounts] = useState<
    Record<string, string>
  >({});

  // Estado para edición completa de cualquier gasto recurrente en cualquier momento
  const [editingRuleId, setEditingRuleId] = useState<string | null>(null);
  const [editName, setEditName] = useState<string>('');
  const [editMerchant, setEditMerchant] = useState<string>('');
  const [editRuc, setEditRuc] = useState<string>('');
  const [editCategory, setEditCategory] =
    useState<ExpenseCategory>('UTILITIES');
  const [editFrequency, setEditFrequency] =
    useState<InflowFrequency>('MONTHLY');
  const [editCustomDays, setEditCustomDays] = useState<number>(30);
  const [editDefaultSoles, setEditDefaultSoles] = useState<string>('');
  const [editDay, setEditDay] = useState<number>(15);
  const [editIsInevitable, setEditIsInevitable] = useState<boolean>(true);
  const [editValidityMode, setEditValidityMode] =
    useState<RecurringValidityMode>('INDEFINITE');
  const [editExpirationDateIso, setEditExpirationDateIso] =
    useState<string>('');
  const [editSelectedCardId, setEditSelectedCardId] = useState<string>('');
  const [editIsActive, setEditIsActive] = useState<boolean>(true);

  const recurringExpensesList = cycleState.recurringExpenses ?? [];
  const snapshot = computeSolTemporalKpis(cycleState);

  let totalSelectedMonthCents = 0;
  let inevitableSelectedMonthCents = 0;
  let controllableSelectedMonthCents = 0;
  let cardRecurringSelectedMonthCents = 0;

  for (const rule of recurringExpensesList) {
    if (!isRecurringExpenseApplicableInCycle(rule, cycleState.startDateIso)) {
      continue;
    }
    const amt = getRecurringExpenseAmountForMonthCents(rule, selectedMonthIso);
    totalSelectedMonthCents += amt;
    if (rule.isCreditCardExpense) {
      cardRecurringSelectedMonthCents += amt;
    }
    if (rule.isInevitable) {
      inevitableSelectedMonthCents += amt;
    } else {
      controllableSelectedMonthCents += amt;
    }
  }

  const handleCreateSubmit = (e: FormEvent) => {
    e.preventDefault();
    const amount = Number(rexpDefaultSoles) || 0;
    if (amount <= 0) return;

    const cleanName = rexpName.trim() || 'Gasto Recurrente';
    const cleanMerchant = rexpMerchant.trim() || cleanName;
    const isCardExp = rexpSelectedCardId.trim().length > 0;

    onAddRecurringExpense({
      name: cleanName,
      merchantName: cleanMerchant,
      issuerRuc: rexpRuc.trim() || undefined,
      category: rexpCategory,
      frequency: rexpFrequency,
      customIntervalDays:
        rexpFrequency === 'CUSTOM_DAYS'
          ? Math.max(1, rexpCustomDays)
          : undefined,
      defaultAmountSoles: amount,
      billingDayOfMonth: rexpDay,
      isInevitable: rexpIsInevitable,
      validityMode: rexpValidityMode,
      expirationDateIso:
        rexpValidityMode === 'EXPIRES_ON_DATE' &&
        rexpExpirationDateIso.trim().length > 0
          ? rexpExpirationDateIso.trim().slice(0, 10)
          : null,
      isCreditCardExpense: isCardExp,
      creditCardId: isCardExp ? rexpSelectedCardId.trim() : null,
    });

    setRexpName('');
    setRexpMerchant('');
    setRexpRuc('');
    setRexpDefaultSoles('');
    setRexpValidityMode('INDEFINITE');
    setRexpExpirationDateIso('');
    setRexpSelectedCardId('');
  };

  const startFullEdit = (rule: RecurringExpenseItem) => {
    setEditingRuleId(rule.id);
    setEditName(rule.name);
    setEditMerchant(rule.merchantName);
    setEditRuc(rule.issuerRuc ?? '');
    setEditCategory(rule.category);
    setEditFrequency(rule.frequency ?? 'MONTHLY');
    setEditCustomDays(rule.customIntervalDays ?? 30);
    setEditDefaultSoles(String(centsToSoles(rule.defaultAmountCents)));
    setEditDay(rule.billingDayOfMonth);
    setEditIsInevitable(rule.isInevitable);
    setEditValidityMode(rule.validityMode ?? 'INDEFINITE');
    setEditExpirationDateIso(rule.expirationDateIso ?? '');
    setEditSelectedCardId(
      rule.isCreditCardExpense && rule.creditCardId ? rule.creditCardId : ''
    );
    setEditIsActive(rule.isActive);
  };

  const handleSaveFullEdit = (ruleId: string) => {
    const amount = Number(editDefaultSoles) || 0;
    if (amount <= 0) return;
    const cleanName = editName.trim() || 'Gasto Recurrente';
    const cleanMerchant = editMerchant.trim() || cleanName;
    const isCardExp = editSelectedCardId.trim().length > 0;

    onUpdateRecurringExpense(ruleId, {
      name: cleanName,
      merchantName: cleanMerchant,
      issuerRuc: editRuc.trim() ? editRuc.trim() : null,
      category: editCategory,
      frequency: editFrequency,
      customIntervalDays:
        editFrequency === 'CUSTOM_DAYS'
          ? Math.max(1, editCustomDays)
          : undefined,
      defaultAmountSoles: amount,
      billingDayOfMonth: editDay,
      isInevitable: editIsInevitable,
      validityMode: editValidityMode,
      expirationDateIso:
        editValidityMode === 'EXPIRES_ON_DATE' &&
        editExpirationDateIso.trim().length > 0
          ? editExpirationDateIso.trim().slice(0, 10)
          : null,
      isCreditCardExpense: isCardExp,
      creditCardId: isCardExp ? editSelectedCardId.trim() : null,
      isActive: editIsActive,
    });

    setEditingRuleId(null);
  };

  return (
    <div className={styles.container}>
      {/* 1. Cabecera y Resumen del Mes Seleccionado */}
      <section className={styles.topSummaryCard}>
        <div className={styles.topHeaderRow}>
          <div>
            <span className={styles.eyebrow}>
              CICLO {cycleState.startDateIso.slice(0, 10)} · MES {selectedMonthIso}
            </span>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
              <h2 className={styles.title}>Gastos Recurrentes</h2>
              <InfoPopover title="Descuento Inmediato de Recurrentes">
                Todos los gastos recurrentes activos se descuentan automáticamente al iniciar o renovar cada ciclo ({cycleState.startDateIso.slice(0, 10)}), sin importar su fecha referencial de pago, para mostrar tu presupuesto libre real desde el Día 1.
              </InfoPopover>
            </div>
          </div>

          {onOpenInflowsView && (
            <button
              type="button"
              className={styles.switchViewBtn}
              onClick={onOpenInflowsView}
            >
              💰 Ir a Ciclo y Tarjetas →
            </button>
          )}
        </div>

        <div className={styles.kpiStrip}>
          <div className={styles.kpiBox}>
            <span className={styles.kpiLabel}>Recurrentes Vigentes</span>
            <span className={styles.kpiValue}>
              {formatSolesFromCents(totalSelectedMonthCents)}
            </span>
            <span className={styles.kpiHint}>
              {recurringExpensesList.filter((r) => r.isActive).length} activo(s) · Inevitables: {formatSolesFromCents(inevitableSelectedMonthCents)}
            </span>
          </div>

          <div className={styles.kpiBox}>
            <span className={styles.kpiLabel}>En Tarjetas</span>
            <span className={styles.kpiValue} style={{ color: '#fbbf24' }}>
              {formatSolesFromCents(cardRecurringSelectedMonthCents)}
            </span>
            <span className={styles.kpiHint}>
              Cargados a tarjeta sin doble resta
            </span>
          </div>

          <div className={styles.kpiBox}>
            <span className={styles.kpiLabel}>Disponible Neto</span>
            <span className={styles.kpiValue} style={{ color: '#34d399' }}>
              {formatSolesFromCents(snapshot.remainingCapCents)}
            </span>
            <span className={styles.kpiHint}>
              Controlables: {formatSolesFromCents(controllableSelectedMonthCents)}
            </span>
          </div>
        </div>

        {/* Selector de Mes Optimizado para Celular y PC */}
        <div className={styles.monthNavigatorBar}>
          <span className={styles.label} style={{ color: 'var(--text-primary)' }}>
            📅 Mes:
          </span>

          <div className={styles.monthNavControls}>
            <button
              type="button"
              className={styles.monthStepBtn}
              onClick={() =>
                setSelectedMonthIso((prev) => shiftMonthIso(prev, -1))
              }
            >
              ◀ Anterior
            </button>

            <input
              type="month"
              value={selectedMonthIso}
              onChange={(e) =>
                setSelectedMonthIso(e.target.value || activeCycleMonthIso)
              }
              className={styles.monthInput}
              aria-label="Seleccionar mes a consultar o modificar"
            />

            <button
              type="button"
              className={styles.monthStepBtn}
              onClick={() => setSelectedMonthIso(activeCycleMonthIso)}
            >
              Actual ({activeCycleMonthIso})
            </button>

            <button
              type="button"
              className={styles.monthStepBtn}
              onClick={() =>
                setSelectedMonthIso((prev) => shiftMonthIso(prev, 1))
              }
            >
              Siguiente ▶
            </button>
          </div>
        </div>
      </section>

      {/* 2. Grilla Principal: Formulario de Creación + Lista Editable de Gastos Recurrentes */}
      <div className={styles.mainGrid}>
        {/* Columna Izquierda: Formulario de Creación */}
        <div className={styles.card}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
            <h3 className={styles.cardTitle}>+ Nuevo Gasto Recurrente</h3>
            <InfoPopover title="Registro de Gasto Recurrente">
              Configura el monto base, frecuencia, vigencia (indefinido o con vencimiento) y si se carga a una tarjeta de crédito.
            </InfoPopover>
          </div>

          <form onSubmit={handleCreateSubmit} className={styles.formStack}>
            <div className={styles.fieldGroup}>
              <label htmlFor="rexp-name" className={styles.label}>
                Concepto *
              </label>
              <input
                id="rexp-name"
                type="text"
                placeholder="Ej. Luz del Sur, Alquiler, Internet"
                value={rexpName}
                onChange={(e) => setRexpName(e.target.value)}
                className={styles.input}
                required
              />
            </div>

            <div className={styles.fieldRow}>
              <div className={styles.fieldGroup}>
                <label htmlFor="rexp-merchant" className={styles.label}>
                  Comercio (Opcional)
                </label>
                <input
                  id="rexp-merchant"
                  type="text"
                  placeholder="Igual al concepto si se deja vacío"
                  value={rexpMerchant}
                  onChange={(e) => setRexpMerchant(e.target.value)}
                  className={styles.input}
                />
              </div>

              <div className={styles.fieldGroup}>
                <label htmlFor="rexp-ruc" className={styles.label}>
                  RUC SUNAT (Opcional)
                </label>
                <input
                  id="rexp-ruc"
                  type="text"
                  placeholder="Ej. 20331898008"
                  value={rexpRuc}
                  onChange={(e) => setRexpRuc(e.target.value)}
                  className={styles.input}
                />
              </div>
            </div>

            <div className={styles.fieldRow}>
              <div className={styles.fieldGroup}>
                <label htmlFor="rexp-category" className={styles.label}>
                  Categoría
                </label>
                <select
                  id="rexp-category"
                  value={rexpCategory}
                  onChange={(e) =>
                    setRexpCategory(e.target.value as ExpenseCategory)
                  }
                  className={styles.select}
                >
                  {(Object.keys(EXPENSE_CATEGORY_LABELS) as ExpenseCategory[]).map(
                    (catKey) => (
                      <option key={catKey} value={catKey}>
                        {EXPENSE_CATEGORY_LABELS[catKey]}
                      </option>
                    )
                  )}
                </select>
              </div>

              <div className={styles.fieldGroup}>
                <label htmlFor="rexp-amount" className={styles.label}>
                  Monto Base (S/) *
                </label>
                <input
                  id="rexp-amount"
                  type="number"
                  min="0.01"
                  step="0.01"
                  placeholder="Ej. 185.50"
                  value={rexpDefaultSoles}
                  onChange={(e) => setRexpDefaultSoles(e.target.value)}
                  className={styles.input}
                  required
                />
              </div>
            </div>

            <div className={styles.fieldRow}>
              <div className={styles.fieldGroup}>
                <label htmlFor="rexp-freq" className={styles.label}>
                  Frecuencia
                </label>
                <select
                  id="rexp-freq"
                  value={rexpFrequency}
                  onChange={(e) =>
                    setRexpFrequency(e.target.value as InflowFrequency)
                  }
                  className={styles.select}
                >
                  <option value="MONTHLY">Mensual</option>
                  <option value="BIWEEKLY">Quincenal</option>
                  <option value="WEEKLY">Semanal</option>
                  <option value="CUSTOM_DAYS">Cada N días</option>
                </select>
              </div>

              {rexpFrequency === 'CUSTOM_DAYS' ? (
                <div className={styles.fieldGroup}>
                  <label htmlFor="rexp-custom-days" className={styles.label}>
                    Cada cuántos días
                  </label>
                  <input
                    id="rexp-custom-days"
                    type="number"
                    min={1}
                    max={365}
                    value={rexpCustomDays}
                    onChange={(e) =>
                      setRexpCustomDays(Number(e.target.value) || 30)
                    }
                    className={styles.input}
                  />
                </div>
              ) : (
                <div className={styles.fieldGroup}>
                  <label
                    htmlFor="rexp-day"
                    className={styles.label}
                    style={{ display: 'inline-flex', alignItems: 'center', gap: '0.35rem' }}
                  >
                    Día de Pago (1–31)
                    <InfoPopover title="Día de Pago Referencial">
                      Fecha referencial del recibo o cargo. El monto se descuenta preventivamente el Día 1 del ciclo.
                    </InfoPopover>
                  </label>
                  <input
                    id="rexp-day"
                    type="number"
                    min={1}
                    max={31}
                    value={rexpDay}
                    onChange={(e) => setRexpDay(Number(e.target.value) || 15)}
                    className={styles.input}
                  />
                </div>
              )}
            </div>

            <div className={styles.fieldRow}>
              <div className={styles.fieldGroup}>
                <label
                  htmlFor="rexp-validity"
                  className={styles.label}
                  style={{ display: 'inline-flex', alignItems: 'center', gap: '0.35rem' }}
                >
                  Vigencia
                  <InfoPopover title="Vigencia del Recurrente">
                    Elige Indefinido para servicios permanentes o Con vencimiento para cuotas y suscripciones temporales.
                  </InfoPopover>
                </label>
                <select
                  id="rexp-validity"
                  value={rexpValidityMode}
                  onChange={(e) =>
                    setRexpValidityMode(e.target.value as RecurringValidityMode)
                  }
                  className={styles.select}
                >
                  <option value="INDEFINITE">♾️ Indefinido</option>
                  <option value="EXPIRES_ON_DATE">📅 Con vencimiento</option>
                </select>
              </div>

              {rexpValidityMode === 'EXPIRES_ON_DATE' ? (
                <div className={styles.fieldGroup}>
                  <label htmlFor="rexp-exp-date" className={styles.label}>
                    Vencimiento *
                  </label>
                  <input
                    id="rexp-exp-date"
                    type="date"
                    value={rexpExpirationDateIso}
                    onChange={(e) => setRexpExpirationDateIso(e.target.value)}
                    className={styles.input}
                    required
                  />
                </div>
              ) : (
                <div className={styles.fieldGroup}>
                  <label
                    htmlFor="rexp-card-select"
                    className={styles.label}
                    style={{ display: 'inline-flex', alignItems: 'center', gap: '0.35rem' }}
                  >
                    Tarjeta o Medio
                    <InfoPopover title="Vínculo a Tarjeta sin Doble Descuento">
                      Si se carga a una tarjeta de crédito, se descuenta al iniciar el ciclo y se suma al consumo de esa tarjeta sin restarse dos veces de tu disponible global.
                    </InfoPopover>
                  </label>
                  <select
                    id="rexp-card-select"
                    value={rexpSelectedCardId}
                    onChange={(e) => setRexpSelectedCardId(e.target.value)}
                    className={styles.select}
                  >
                    <option value="">💵 Débito / Efectivo</option>
                    {creditCards.map((card) => (
                      <option key={card.id} value={card.id}>
                        💳 {card.alias}
                        {card.issuerBank ? ` (${card.issuerBank})` : ''}
                      </option>
                    ))}
                  </select>
                </div>
              )}
            </div>

            {rexpValidityMode === 'EXPIRES_ON_DATE' && (
              <div className={styles.fieldGroup}>
                <label
                  htmlFor="rexp-card-select-exp"
                  className={styles.label}
                  style={{ display: 'inline-flex', alignItems: 'center', gap: '0.35rem' }}
                >
                  Tarjeta o Medio
                  <InfoPopover title="Vínculo a Tarjeta sin Doble Descuento">
                    Si se carga a una tarjeta de crédito, se descuenta al iniciar el ciclo y se suma al consumo de esa tarjeta sin restarse dos veces de tu disponible global.
                  </InfoPopover>
                </label>
                <select
                  id="rexp-card-select-exp"
                  value={rexpSelectedCardId}
                  onChange={(e) => setRexpSelectedCardId(e.target.value)}
                  className={styles.select}
                >
                  <option value="">💵 Débito / Efectivo</option>
                  {creditCards.map((card) => (
                    <option key={card.id} value={card.id}>
                      💳 {card.alias}
                      {card.issuerBank ? ` (${card.issuerBank})` : ''}
                    </option>
                  ))}
                </select>
              </div>
            )}

            <label className={styles.inevitableCheckboxBox}>
              <input
                type="checkbox"
                checked={rexpIsInevitable}
                onChange={(e) => setRexpIsInevitable(e.target.checked)}
                className={styles.inevitableCheckboxInput}
              />
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.35rem' }}>
                <span className={styles.ruleTitle} style={{ fontSize: '0.84rem' }}>
                  🔒 Gasto Inevitable
                </span>
                <InfoPopover title="Gasto Inevitable vs. Controlable">
                  Los pagos inevitables (alquiler, luz, agua) se descuentan el Día 1 del ciclo y se excluyen de los rankings de gastos controlables.
                </InfoPopover>
              </div>
            </label>

            <button type="submit" className={styles.primaryBtn}>
              + Guardar Recurrente
            </button>
          </form>
        </div>

        {/* Columna Derecha: Lista de Gastos Recurrentes Configurados (Edición Total + Ajuste por Mes) */}
        <div className={styles.card}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
            <h3 className={styles.cardTitle}>
              Mis Gastos Recurrentes ({recurringExpensesList.length})
            </h3>
            <InfoPopover title="Gestión y Ajuste por Mes">
              Todos los gastos activos y vigentes se descuentan al iniciar el ciclo ({cycleState.startDateIso.slice(0, 10)}). Puedes ajustar el monto del mes {selectedMonthIso} o pulsar Editar Todo.
            </InfoPopover>
          </div>

          <div className={styles.rulesList}>
            {recurringExpensesList.length === 0 ? (
              <div className={styles.emptyStateBox}>
                Aún no tienes gastos recurrentes configurados. Usa el formulario para registrar tus pagos fijos o servicios mensuales.
              </div>
            ) : (
              recurringExpensesList.map((rule) => {
                const effectiveCents = getRecurringExpenseAmountForMonthCents(
                  rule,
                  selectedMonthIso
                );
                const effectiveSoles = centsToSoles(effectiveCents);
                const hasCustomMonthOverride =
                  rule.monthlyOverridesCents[selectedMonthIso] !== undefined &&
                  rule.monthlyOverridesCents[selectedMonthIso] !==
                    rule.defaultAmountCents;

                const draftKey = `${rule.id}::${selectedMonthIso}`;
                const currentInputVal =
                  draftMonthAmounts[draftKey] ?? String(effectiveSoles);
                const isEditingFull = editingRuleId === rule.id;

                const linkedCard =
                  rule.isCreditCardExpense && rule.creditCardId
                    ? creditCards.find((c) => c.id === rule.creditCardId) ?? null
                    : null;

                const isExpiredForCurrentCycle =
                  rule.validityMode === 'EXPIRES_ON_DATE' &&
                  Boolean(rule.expirationDateIso) &&
                  (rule.expirationDateIso ?? '').slice(0, 10) <
                    cycleState.startDateIso.slice(0, 10);

                return (
                  <div
                    key={rule.id}
                    className={`${styles.ruleCard} ${
                      !rule.isActive || isExpiredForCurrentCycle
                        ? styles.ruleCardPaused
                        : ''
                    }`}
                  >
                    <div className={styles.ruleHeader}>
                      <div>
                        <div className={styles.ruleTitle}>{rule.name}</div>
                        <div className={styles.ruleMeta}>
                          {rule.merchantName}
                          {rule.issuerRuc ? ` · RUC ${rule.issuerRuc}` : ''} ·{' '}
                          {EXPENSE_CATEGORY_LABELS[rule.category]} ·{' '}
                          {INFLOW_FREQUENCY_LABELS[rule.frequency ?? 'MONTHLY']} · Pago ref. día{' '}
                          {rule.billingDayOfMonth} ·{' '}
                          <strong style={{ color: '#34d399' }}>
                            ⚡ Descontado al iniciar ciclo ({cycleState.startDateIso.slice(0, 10)})
                          </strong>
                        </div>

                        <div className={styles.badgesRow}>
                          <button
                            type="button"
                            className={`${styles.inevitableToggleBtn} ${
                              rule.isInevitable
                                ? styles.inevitableActive
                                : styles.inevitableControllable
                            }`}
                            onClick={() =>
                              onUpdateRecurringExpenseForMonth({
                                ruleId: rule.id,
                                monthIso: selectedMonthIso,
                                amountSoles: effectiveSoles,
                                applyAsNewDefault: false,
                                isInevitable: !rule.isInevitable,
                              })
                            }
                            title="Haz clic para alternar entre Inevitable y Controlable"
                          >
                            {rule.isInevitable
                              ? '🔒 Inevitable (Cambiar a Controlable)'
                              : '🎯 Controlable (Cambiar a Inevitable)'}
                          </button>

                          {/* Badge de Tarjeta Asociada vs Débito/Efectivo */}
                          <span className={styles.baseBadge}>
                            {linkedCard
                              ? `💳 ${linkedCard.alias}`
                              : rule.isCreditCardExpense && rule.creditCardId
                                ? `💳 Tarjeta (${rule.creditCardId})`
                                : '💵 Débito/Efectivo'}
                          </span>

                          {/* Badge de Vigencia */}
                          {rule.validityMode === 'EXPIRES_ON_DATE' &&
                          rule.expirationDateIso ? (
                            <span
                              className={
                                isExpiredForCurrentCycle
                                  ? styles.pausedBadge
                                  : styles.overrideBadge
                              }
                            >
                              {isExpiredForCurrentCycle
                                ? `⌛ Vencido (${rule.expirationDateIso.slice(0, 10)})`
                                : `📅 Vence: ${rule.expirationDateIso.slice(0, 10)}`}
                            </span>
                          ) : (
                            <span className={styles.baseBadge}>♾️ Indefinido</span>
                          )}

                          {!rule.isActive && (
                            <span className={styles.pausedBadge}>⏸️ PAUSADO</span>
                          )}
                        </div>
                      </div>

                      <div style={{ textAlign: 'right' }}>
                        <span className={styles.amountDisplay}>
                          -{formatSolesFromCents(effectiveCents)}
                        </span>
                        {hasCustomMonthOverride ? (
                          <span className={styles.overrideBadge}>
                            Modificado en {selectedMonthIso} (Base:{' '}
                            {formatSolesFromCents(rule.defaultAmountCents)})
                          </span>
                        ) : (
                          <span className={styles.baseBadge}>
                            Monto Base ({formatSolesFromCents(rule.defaultAmountCents)})
                          </span>
                        )}
                      </div>
                    </div>

                    {/* Sección A: Modificar Monto Específico del Mes Seleccionado */}
                    <div className={styles.monthEditorRow}>
                      <span className={styles.ruleMeta}>
                        Monto en <strong>{selectedMonthIso}</strong> (S/):
                      </span>

                      <div className={styles.monthEditorControls}>
                        <input
                          type="number"
                          min="0"
                          step="0.01"
                          value={currentInputVal}
                          onChange={(e) =>
                            setDraftMonthAmounts((prev) => ({
                              ...prev,
                              [draftKey]: e.target.value,
                            }))
                          }
                          className={styles.input}
                          style={{ width: '115px' }}
                          aria-label={`Monto de ${rule.name} para ${selectedMonthIso}`}
                        />

                        <button
                          type="button"
                          className={styles.secondaryBtn}
                          onClick={() => {
                            const nextSoles = Math.max(
                              0,
                              Number(currentInputVal) || 0
                            );
                            onUpdateRecurringExpenseForMonth({
                              ruleId: rule.id,
                              monthIso: selectedMonthIso,
                              amountSoles: nextSoles,
                              applyAsNewDefault: false,
                            });
                          }}
                        >
                          💾 Guardar en {selectedMonthIso}
                        </button>

                        <button
                          type="button"
                          className={styles.neutralBtn}
                          onClick={() => {
                            const nextSoles = Math.max(
                              0,
                              Number(currentInputVal) || 0
                            );
                            onUpdateRecurringExpenseForMonth({
                              ruleId: rule.id,
                              monthIso: selectedMonthIso,
                              amountSoles: nextSoles,
                              applyAsNewDefault: true,
                            });
                          }}
                        >
                          📌 Fijar como Base
                        </button>

                        {hasCustomMonthOverride && (
                          <button
                            type="button"
                            className={styles.neutralBtn}
                            onClick={() => {
                              const baseSoles = centsToSoles(rule.defaultAmountCents);
                              setDraftMonthAmounts((prev) => ({
                                ...prev,
                                [draftKey]: String(baseSoles),
                              }));
                              onUpdateRecurringExpenseForMonth({
                                ruleId: rule.id,
                                monthIso: selectedMonthIso,
                                amountSoles: baseSoles,
                                applyAsNewDefault: false,
                                clearMonthOverride: true,
                              });
                            }}
                          >
                            ↩️ Restaurar Base
                          </button>
                        )}
                      </div>
                    </div>

                    {/* Sección B: Acciones de Edición Total, Pausar/Activar y Eliminar */}
                    <div className={styles.ruleActionsFooter}>
                      <div style={{ display: 'flex', gap: '0.45rem', flexWrap: 'wrap' }}>
                        <button
                          type="button"
                          className={styles.neutralBtn}
                          onClick={() =>
                            isEditingFull
                              ? setEditingRuleId(null)
                              : startFullEdit(rule)
                          }
                        >
                          {isEditingFull ? '✕ Cerrar Edición' : '✏️ Editar Todo'}
                        </button>

                        <button
                          type="button"
                          className={styles.neutralBtn}
                          onClick={() =>
                            onUpdateRecurringExpense(rule.id, {
                              name: rule.name,
                              merchantName: rule.merchantName,
                              issuerRuc: rule.issuerRuc,
                              category: rule.category,
                              frequency: rule.frequency ?? 'MONTHLY',
                              customIntervalDays: rule.customIntervalDays,
                              defaultAmountSoles: centsToSoles(
                                rule.defaultAmountCents
                              ),
                              billingDayOfMonth: rule.billingDayOfMonth,
                              isInevitable: rule.isInevitable,
                              validityMode: rule.validityMode ?? 'INDEFINITE',
                              expirationDateIso: rule.expirationDateIso ?? null,
                              isCreditCardExpense: Boolean(
                                rule.isCreditCardExpense
                              ),
                              creditCardId: rule.creditCardId ?? null,
                              isActive: !rule.isActive,
                            })
                          }
                        >
                          {rule.isActive ? '⏸️ Pausar' : '▶️ Activar'}
                        </button>
                      </div>

                      <button
                        type="button"
                        className={styles.dangerBtn}
                        onClick={() => onDeleteRecurringExpense(rule.id)}
                      >
                        🗑️ Eliminar
                      </button>
                    </div>

                    {/* Panel Desplegable de Edición Completa en Cualquier Momento */}
                    {isEditingFull && (
                      <div className={styles.fullEditBox}>
                        <strong style={{ fontSize: '0.8rem', color: 'var(--accent-primary)' }}>
                          ✏️ Edición Completa del Gasto Recurrente
                        </strong>

                        <div className={styles.fieldRow}>
                          <div className={styles.fieldGroup}>
                            <label className={styles.label}>Concepto / Nombre</label>
                            <input
                              type="text"
                              value={editName}
                              onChange={(e) => setEditName(e.target.value)}
                              className={styles.input}
                            />
                          </div>

                          <div className={styles.fieldGroup}>
                            <label className={styles.label}>
                              Establecimiento / Razón Social
                            </label>
                            <input
                              type="text"
                              value={editMerchant}
                              onChange={(e) => setEditMerchant(e.target.value)}
                              className={styles.input}
                            />
                          </div>
                        </div>

                        <div className={styles.fieldRowThree}>
                          <div className={styles.fieldGroup}>
                            <label className={styles.label}>RUC SUNAT</label>
                            <input
                              type="text"
                              value={editRuc}
                              onChange={(e) => setEditRuc(e.target.value)}
                              className={styles.input}
                              placeholder="Opcional"
                            />
                          </div>

                          <div className={styles.fieldGroup}>
                            <label className={styles.label}>Categoría</label>
                            <select
                              value={editCategory}
                              onChange={(e) =>
                                setEditCategory(e.target.value as ExpenseCategory)
                              }
                              className={styles.select}
                            >
                              {(
                                Object.keys(
                                  EXPENSE_CATEGORY_LABELS
                                ) as ExpenseCategory[]
                              ).map((catKey) => (
                                <option key={catKey} value={catKey}>
                                  {EXPENSE_CATEGORY_LABELS[catKey]}
                                </option>
                              ))}
                            </select>
                          </div>

                          <div className={styles.fieldGroup}>
                            <label className={styles.label}>Monto Base (S/)</label>
                            <input
                              type="number"
                              min="0.01"
                              step="0.01"
                              value={editDefaultSoles}
                              onChange={(e) => setEditDefaultSoles(e.target.value)}
                              className={styles.input}
                            />
                          </div>
                        </div>

                        <div className={styles.fieldRowThree}>
                          <div className={styles.fieldGroup}>
                            <label className={styles.label}>Frecuencia</label>
                            <select
                              value={editFrequency}
                              onChange={(e) =>
                                setEditFrequency(e.target.value as InflowFrequency)
                              }
                              className={styles.select}
                            >
                              <option value="MONTHLY">Mensual</option>
                              <option value="BIWEEKLY">Quincenal</option>
                              <option value="WEEKLY">Semanal</option>
                              <option value="CUSTOM_DAYS">Cada N días</option>
                            </select>
                          </div>

                          <div className={styles.fieldGroup}>
                            <label className={styles.label}>
                              Día de Pago (1–31)
                            </label>
                            <input
                              type="number"
                              min={1}
                              max={31}
                              value={editDay}
                              onChange={(e) =>
                                setEditDay(Number(e.target.value) || 15)
                              }
                              className={styles.input}
                            />
                          </div>

                          <div className={styles.fieldGroup}>
                            <label className={styles.label}>Tipo de Control</label>
                            <select
                              value={editIsInevitable ? 'INEVITABLE' : 'CONTROLLABLE'}
                              onChange={(e) =>
                                setEditIsInevitable(
                                  e.target.value === 'INEVITABLE'
                                )
                              }
                              className={styles.select}
                            >
                              <option value="INEVITABLE">🔒 Inevitable</option>
                              <option value="CONTROLLABLE">🎯 Controlable</option>
                            </select>
                          </div>
                        </div>

                        <div className={styles.fieldRow}>
                          <div className={styles.fieldGroup}>
                            <label className={styles.label}>Vigencia</label>
                            <select
                              value={editValidityMode}
                              onChange={(e) =>
                                setEditValidityMode(
                                  e.target.value as RecurringValidityMode
                                )
                              }
                              className={styles.select}
                            >
                              <option value="INDEFINITE">♾️ Indefinido</option>
                              <option value="EXPIRES_ON_DATE">
                                📅 Con Fecha de Vencimiento
                              </option>
                            </select>
                          </div>

                          {editValidityMode === 'EXPIRES_ON_DATE' && (
                            <div className={styles.fieldGroup}>
                              <label className={styles.label}>
                                Fecha de Vencimiento
                              </label>
                              <input
                                type="date"
                                value={editExpirationDateIso}
                                onChange={(e) =>
                                  setEditExpirationDateIso(e.target.value)
                                }
                                className={styles.input}
                              />
                            </div>
                          )}

                          <div className={styles.fieldGroup}>
                            <label className={styles.label}>
                              ¿Se carga a una Tarjeta de Crédito?
                            </label>
                            <select
                              value={editSelectedCardId}
                              onChange={(e) =>
                                setEditSelectedCardId(e.target.value)
                              }
                              className={styles.select}
                            >
                              <option value="">
                                💵 No — Débito / Efectivo
                              </option>
                              {creditCards.map((card) => (
                                <option key={card.id} value={card.id}>
                                  💳 Sí — {card.alias}
                                  {card.issuerBank ? ` (${card.issuerBank})` : ''}
                                </option>
                              ))}
                            </select>
                          </div>
                        </div>

                        <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                          <button
                            type="button"
                            className={styles.primaryBtn}
                            onClick={() => handleSaveFullEdit(rule.id)}
                          >
                            💾 Guardar Todos los Cambios
                          </button>
                          <button
                            type="button"
                            className={styles.neutralBtn}
                            onClick={() => setEditingRuleId(null)}
                          >
                            Cancelar
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
